import {
  formatNumberV2,
  formatDateISO,
  formatDate,
  ceil2Decimal,
  formatDateFromUnix,
  getMinuteTimeV2,
  formatDateISOToTh,
  formatNumberDigit,
} from "../../utilities/Format";
import { format } from "date-fns";
import { OperationTime } from "../../constants/Enum";
import { AvgFwtByLot, DgtPdOrderOperationLog } from "../../models";
import { groupBy } from "lodash";
import { OrderInspectionLogColumns, OrderReceiptColumns } from "../../constants/Column";
import { OrderInspectionLog, OrderReceipt } from "../types";
import { calculateYieldFilling, calculateYieldPacking } from "../../logic/yield";

function pdfDocumentFormat(props: any) {
  const domain = window.location.origin;
  const logoNeoF = domain + "/images/NEO.png";

  // Production Logs
  const dataOrderConfirmOp = [];
  for (const operation of props.operations) {
    const logs = props.operationLogs.filter(
      (log: any) => log.operationNo === operation.operationNo
    );
    const totalYield = ceil2Decimal(
      logs.reduce((prev: any, { yield: y }: any) => prev + (y || 0), 0)
    );

    const dataOrderConfirmOpLogs = [];
    for (const log of logs) {
      const dataOrderConfirmOpTotalTime = () => {
        if (log.timeType === OperationTime.DOWN_TIME) {
          if (!log.totalTime) return "-";
          if (!log.downtimeBaseTime)
            return `${getMinuteTimeV2(log.startedTime, log.finishedTime)} นาที`;
          return `${getRemainTime(log)} นาที`;
        }
        return log.totalTime
          ? `${getMinuteTimeV2(log.startedTime, log.finishedTime)} นาที`
          : "-";
      };

      const dataOrderConfirmOpRT = () => {
        if (log.timeType === OperationTime.DOWN_TIME) {
          return log.totalTime! > 0 && log.totalEmployeeRT! > 0
            ? `${log.totalEmployeeRT} คน/${
                getRemainTime(log) * log.totalEmployeeRT!
              } นาที`
            : "-";
        }
        return log.totalTime! >= 0 && log.totalEmployeeRT! > 0
          ? `${log.totalEmployeeRT} คน/${
              getMinuteTimeV2(log.startedTime, log.finishedTime) *
              log.totalEmployeeRT!
            } นาที`
          : "-";
      };

      const dataOrderConfirmOpOT = () => {
        if (log.timeType === OperationTime.DOWN_TIME) {
          return log.totalTime! > 0 && log.totalEmployeeOT! > 0
            ? `${log.totalEmployeeOT} คน/${
                getRemainTime(log) * log.totalEmployeeOT!
              } นาที`
            : "-";
        }
        return log.totalTime! >= 0 && log.totalEmployeeOT! > 0
          ? `${log.totalEmployeeOT} คน/${
              getMinuteTimeV2(log.startedTime, log.finishedTime) *
              log.totalEmployeeOT!
            } นาที`
          : "-";
      };

      const dataOrderConfirmOpYield = () => {
        if (log.timeType === OperationTime.SOAP_BASE) {
          return log.bulkUsedQty ? formatNumberV2(log.bulkUsedQty) : "-";
        }
        return log.yield ? formatNumberV2(log.yield) : "-";
      };

      dataOrderConfirmOpLogs.push([
        {
          text: log.timeType ? log.timeType : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.batchNo ? log.batchNo : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.mch ? log.mch : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.split ? log.split : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.postingDate
            ? format(new Date(log.postingDate), "dd-MM-yyyy")
            : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.shift ? log.shift : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.startedTime
            ? format(new Date(log.startedTime * 1000), "HH:mm")
            : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.finishedTime
            ? format(new Date(log.finishedTime * 1000), "HH:mm")
            : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: dataOrderConfirmOpTotalTime(),
          style: ["textBody", "textSmall"],
        },
        {
          text: dataOrderConfirmOpRT(),
          style: ["textBody", "textSmall"],
        },
        {
          text: dataOrderConfirmOpOT(),
          style: ["textBody", "textSmall"],
        },
        {
          text: dataOrderConfirmOpYield(),
          style: ["textBody", "textSmall"],
        },
        {
          text: log.scrap ? formatNumberV2(log.scrap) : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.bulkUsedQty ? formatNumberV2(log.bulkUsedQty) : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.createdBy ? log.createdBy.split("@")[0] : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.confirmedBy ? log.confirmedBy.split("@")[0] : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: log.confirmedDate ? formatDateISO(log.confirmedDate) : "-",
          style: ["textBody", "textSmall"],
        },
      ]);
    }

    dataOrderConfirmOp.push(
      {
        text: `OP. ${operation.operationNo} - ${
          operation.actualWorkCenterDescription
            ? operation.actualWorkCenterDescription
            : operation.workCenterDescription
        } (${
          operation.actualWorkCenterId
            ? operation.actualWorkCenterId
            : operation.workCenterId
        })`,
        style: "textSubTitle",
      },
      {
        style: "tableBodyP2",
        table: {
          widths: [100, 90, 80, 250, 95, 110],
          body: [
            [
              {
                text: "Confirmation No",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: operation.confirmationId!
                  ? operation.confirmationId!
                  : "-",
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Drafted by",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: operation.draftedBy! ? operation.draftedBy! : "-",
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Drafted time",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: operation.draftedDate
                  ? formatDateISO(operation.draftedDate)
                  : "-",
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Actual Qty",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: totalYield
                  ? `${formatNumberV2(totalYield)} ${props.order.materialUnit}`
                  : "-",
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Confirmed by",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: operation.confirmedBy! ? operation.confirmedBy! : "-",
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Confirmed time",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: operation.confirmedDate
                  ? formatDateISO(operation.confirmedDate)
                  : "-",
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
          ],
        },
      },
      {
        style: "tableSubBodyP2",
        alignment: "center",
        marginTop: 5,
        table: {
          widths: [
            35, 50, 20, 20, 50, 20, 20, 20, 30, 55, 55, 35, 35, 40, 50, 50, 40,
          ],
          body: [
            [
              {
                text: "Type",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Batch No",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Mch",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Split",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Posting Date",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Shift",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "เริ่ม",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "จบ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "เวลารวม",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "RT",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "OT",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ผลิตได้",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ของเสีย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ใช้Bulk",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "บันทึกโดย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ยืนยันโดย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "เวลายืนยัน",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataOrderConfirmOpLogs,
          ],
        },
      }
    );
  }

  // INSPECTION
  const dataInspection = [];
  for (const bulkInspectLog of props.bulkInspectLogs) {
    dataInspection.push([
      {
        text: `${
          bulkInspectLog.createdBy
            ? bulkInspectLog.startedTime
              ? `${
                  bulkInspectLog.createdBy.split("@")[0]
                }\n${formatDateFromUnix(bulkInspectLog.startedTime)}`
              : bulkInspectLog.createdBy.split("@")[0]
            : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
      {
        text: `${
          bulkInspectLog.bulkStatusBy
            ? bulkInspectLog.finishedTime
              ? `${
                  bulkInspectLog.bulkStatusBy.split("@")[0]
                }\n${formatDateFromUnix(bulkInspectLog.finishedTime)}`
              : bulkInspectLog.bulkStatusBy.split("@")[0]
            : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
      {
        text: `${
          bulkInspectLog.totalTime
            ? getMinuteTimeV2(
                bulkInspectLog.startedTime,
                bulkInspectLog.finishedTime
              )
            : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
      {
        text: `${bulkInspectLog.bulkStatus ? bulkInspectLog.bulkStatus : "-"}`,
        style: ["textBody", "textSmall"],
      },
      {
        text: `${
          bulkInspectLog.bulkStatusBy
            ? bulkInspectLog.bulkStatusDateTime
              ? `${
                  bulkInspectLog.bulkStatusBy.split("@")[0]
                }\n${formatDateFromUnix(bulkInspectLog.bulkStatusDateTime)}`
              : bulkInspectLog.bulkStatusBy.split("@")[0]
            : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
      {
        text: `${
          bulkInspectLog.bulkStatusComment
            ? bulkInspectLog.bulkStatusComment.split("@")[0]
            : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
      {
        text: `${
          bulkInspectLog.approveBulkStatusBy
            ? bulkInspectLog.approveBulkStatusDateTime
              ? `${
                  bulkInspectLog.approveBulkStatusBy.split("@")[0]
                }\n${formatDateFromUnix(
                  bulkInspectLog.approveBulkStatusDateTime
                )}`
              : bulkInspectLog.approveBulkStatusBy.split("@")[0]
            : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
    ]);
  }

  const tableInspection = [];
  if (dataInspection.length > 0) {
    tableInspection.push(
      { text: "", pageOrientation: "portrait", pageBreak: "before" },
      {
        text: "INSPECTION",
        style: "textTitle",
      },
      {
        style: "tableBodyP3",
        marginTop: 10,
        table: {
          widths: [65, 65, 65, 45, 65, 120, 40],
          body: [
            [
              {
                text: "เริ่มตรวจเมื่อ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ตรวจเสร็จเมื่อ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "เวลาที่ใช้",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Bulk Status",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "บันทึกเมื่อ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Bulk Comment",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "อนุมัติเมื่อ",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataInspection,
          ],
        },
      }
    );
  }

  // BOM Usage addOnAndSortedMvs
  const dataAddOnAndSortedMvs = [];
  const groupAddOnAndSortedMvs = groupBy(
    props.addOnAndSortedMvs,
    "materialCode"
  );
  for (const groupAddOnAndSortedMv of Object.entries(groupAddOnAndSortedMvs)) {
    const uniqueQtys = new Set(
      (groupAddOnAndSortedMv[1] as []).map(
        (row: any) => row.materialRequestQty || 0
      )
    );
    const sumQty = Array.from(uniqueQtys).reduce(
      (prev: any, qty: any) => prev + qty,
      0
    );
    const LengthGroupAddOnAndSortedMv = (groupAddOnAndSortedMv[1] as []).length;
    let tempCountGroupAddOnAndSortedMv = 0;
    for (const addOnAndSortedMv of groupAddOnAndSortedMv[1] as []) {
      tempCountGroupAddOnAndSortedMv++;
      const dataMaterialRequestQty = () => {
        if (addOnAndSortedMv["materialType"] === "BULK") {
          const tempMaterialRequestQty = (
            groupAddOnAndSortedMv[1] as []
          ).reduce(
            (prev: any, { materialRequestQty }: any) =>
              prev + (materialRequestQty || 0),
            0
          );
          return tempMaterialRequestQty
            ? formatNumberV2(tempMaterialRequestQty)
            : 0;
        }
        return sumQty ? formatNumberV2(sumQty) : 0;
      };
      if (LengthGroupAddOnAndSortedMv > 1) {
        if (tempCountGroupAddOnAndSortedMv === LengthGroupAddOnAndSortedMv) {
          dataAddOnAndSortedMvs.push(
            [
              "",
              "",
              "",
              "",
              {
                text:
                  addOnAndSortedMv["materialType"] === "BULK"
                    ? addOnAndSortedMv["approvedDateTime"]
                      ? formatDateFromUnix(addOnAndSortedMv["approvedDateTime"])
                      : "-"
                    : addOnAndSortedMv["documentCreatedAt"]
                    ? formatDateFromUnix(addOnAndSortedMv["documentCreatedAt"])
                    : "-",
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["usedGoodQty"]
                  ? formatNumberV2(addOnAndSortedMv["usedGoodQty"])
                  : 0,
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["usedWasteQty"]
                  ? formatNumberV2(addOnAndSortedMv["usedWasteQty"])
                  : 0,
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["returnGoodQty"]
                  ? formatNumberV2(addOnAndSortedMv["returnGoodQty"])
                  : 0,
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["returnGoodShrinkQty"]
                  ? formatNumberV2(addOnAndSortedMv["returnGoodShrinkQty"])
                  : 0,
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["returnWasteQty"]
                  ? formatNumberV2(addOnAndSortedMv["returnWasteQty"])
                  : 0,
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["batchLotNo"]
                  ? addOnAndSortedMv["batchLotNo"]
                  : "-",
                style: ["textBody", "textSmall"],
              },
              {
                text: addOnAndSortedMv["isExtra"] ? "เบิกเพิ่ม" : "",
                style: ["textBody", "textSmall", "textYellow"],
              },
            ],
            [
              "",
              "",
              "",
              "",
              {
                text: "Summary",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: formatNumberV2(
                  (groupAddOnAndSortedMv[1] as []).reduce(
                    (prev, { usedGoodQty }) => prev + usedGoodQty!,
                    0
                  )
                ),
                style: ["textBodyBold", "textSmall", "textGreen"],
              },
              {
                text: formatNumberV2(
                  (groupAddOnAndSortedMv[1] as []).reduce(
                    (prev, { usedWasteQty }) => prev + usedWasteQty!,
                    0
                  )
                ),
                style: ["textBodyBold", "textSmall", "textRed"],
              },
              {
                text: formatNumberV2(
                  (groupAddOnAndSortedMv[1] as []).reduce(
                    (prev, { returnGoodQty }) => prev + returnGoodQty!,
                    0
                  )
                ),
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: formatNumberV2(
                  (groupAddOnAndSortedMv[1] as []).reduce(
                    (prev, { returnGoodShrinkQty }) =>
                      prev + returnGoodShrinkQty!,
                    0
                  )
                ),
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: formatNumberV2(
                  (groupAddOnAndSortedMv[1] as []).reduce(
                    (prev, { returnWasteQty }) => prev + returnWasteQty!,
                    0
                  )
                ),
                style: ["textBodyBold", "textSmall"],
              },
              "",
              {
                text: addOnAndSortedMv["isExtra"] ? "เบิกเพิ่ม" : "",
                style: ["textBodyBold", "textSmall", "textYellow"],
              },
            ]
          );
        } else {
          dataAddOnAndSortedMvs.push([
            {
              text: addOnAndSortedMv["materialCode"]
                ? addOnAndSortedMv["materialCode"]
                : "-",
              style: ["textBody", "textSmall"],
              rowSpan: LengthGroupAddOnAndSortedMv + 1,
            },
            {
              text: addOnAndSortedMv["materialDescription"]
                ? addOnAndSortedMv["materialDescription"]
                : "-",
              style: ["textBody", "textSmall"],
              rowSpan: LengthGroupAddOnAndSortedMv + 1,
            },
            {
              text: dataMaterialRequestQty(),
              style: ["textBodyBold", "textSmall"],
              rowSpan: LengthGroupAddOnAndSortedMv + 1,
            },
            {
              text: formatNumberV2(
                (groupAddOnAndSortedMv[1] as []).reduce(
                  (prev: any, { amount }: any) => prev + amount,
                  0
                )
              ),
              style: ["textBodyBold", "textSmall"],
              rowSpan: LengthGroupAddOnAndSortedMv + 1,
            },
            {
              text:
                addOnAndSortedMv["materialType"] === "BULK"
                  ? addOnAndSortedMv["approvedDateTime"]
                    ? formatDateFromUnix(addOnAndSortedMv["approvedDateTime"])
                    : "-"
                  : addOnAndSortedMv["documentCreatedAt"]
                  ? formatDateFromUnix(addOnAndSortedMv["documentCreatedAt"])
                  : "-",
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["usedGoodQty"]
                ? formatNumberV2(addOnAndSortedMv["usedGoodQty"])
                : 0,
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["usedWasteQty"]
                ? formatNumberV2(addOnAndSortedMv["usedWasteQty"])
                : 0,
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["returnGoodQty"]
                ? formatNumberV2(addOnAndSortedMv["returnGoodQty"])
                : 0,
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["returnGoodShrinkQty"]
                ? formatNumberV2(addOnAndSortedMv["returnGoodShrinkQty"])
                : 0,
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["returnWasteQty"]
                ? formatNumberV2(addOnAndSortedMv["returnWasteQty"])
                : 0,
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["batchLotNo"]
                ? addOnAndSortedMv["batchLotNo"]
                : "-",
              style: ["textBody", "textSmall"],
            },
            {
              text: addOnAndSortedMv["isExtra"] ? "เบิกเพิ่ม" : "",
              style: ["textBody", "textSmall", "textYellow"],
            },
          ]);
        }
      } else {
        dataAddOnAndSortedMvs.push([
          {
            text: addOnAndSortedMv["materialCode"]
              ? addOnAndSortedMv["materialCode"]
              : "-",
            style: ["textBody", "textSmall"],
          },
          {
            text: addOnAndSortedMv["materialDescription"]
              ? addOnAndSortedMv["materialDescription"]
              : "-",
            style: ["textBody", "textSmall"],
          },
          {
            text: dataMaterialRequestQty(),
            style: ["textBodyBold", "textSmall"],
          },
          {
            text: formatNumberV2(
              (groupAddOnAndSortedMv[1] as []).reduce(
                (prev: any, { amount }: any) => prev + amount,
                0
              )
            ),
            style: ["textBodyBold", "textSmall"],
          },
          {
            text:
              addOnAndSortedMv["materialType"] === "BULK"
                ? addOnAndSortedMv["approvedDateTime"]
                  ? formatDateFromUnix(addOnAndSortedMv["approvedDateTime"])
                  : "-"
                : addOnAndSortedMv["documentCreatedAt"]
                ? formatDateFromUnix(addOnAndSortedMv["documentCreatedAt"])
                : "-",
            style: ["textBodyBold", "textSmall"],
          },
          {
            text: addOnAndSortedMv["usedGoodQty"]
              ? formatNumberV2(addOnAndSortedMv["usedGoodQty"])
              : 0,
            style: ["textBodyBold", "textSmall", "textGreen"],
          },
          {
            text: addOnAndSortedMv["usedWasteQty"]
              ? formatNumberV2(addOnAndSortedMv["usedWasteQty"])
              : 0,
            style: ["textBodyBold", "textSmall", "textRed"],
          },
          {
            text: addOnAndSortedMv["returnGoodQty"]
              ? formatNumberV2(addOnAndSortedMv["returnGoodQty"])
              : 0,
            style: ["textBodyBold", "textSmall"],
          },
          {
            text: addOnAndSortedMv["returnGoodShrinkQty"]
              ? formatNumberV2(addOnAndSortedMv["returnGoodShrinkQty"])
              : 0,
            style: ["textBodyBold", "textSmall"],
          },
          {
            text: addOnAndSortedMv["returnWasteQty"]
              ? formatNumberV2(addOnAndSortedMv["returnWasteQty"])
              : 0,
            style: ["textBodyBold", "textSmall"],
          },
          {
            text: addOnAndSortedMv["batchLotNo"]
              ? addOnAndSortedMv["batchLotNo"]
              : "-",
            style: ["textBodyBold", "textSmall"],
          },
          {
            text: addOnAndSortedMv["isExtra"] ? "เบิกเพิ่ม" : "",
            style: ["textBodyBold", "textSmall", "textYellow"],
          },
        ]);
      }
    }
  }

  // BOM Usage extras
  const dataBomUsageExtras = [];
  const tableBomUsageExtras = [];
  for (const extra of props.extras) {
    const withdrawalList = () => {
      const items = extra.items ? JSON.parse(extra.items) : [];
      return items.map((itm: any) => {
        return `• ${itm.materialCode} - ${itm.materialDescription} = ${itm.qty} ${itm.materialUnit} \n`;
      });
    };
    dataBomUsageExtras.push([
      {
        text: withdrawalList(),
        style: ["textBody", "textSmall"],
      },
      {
        text: extra.requestedBy
          ? extra.requestedDateTime
            ? `${extra.requestedBy} ${formatDateFromUnix(
                extra.requestedDateTime
              )}`
            : extra.requestedBy
          : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: extra.approvedBy
          ? extra.approvedDateTime
            ? `${extra.approvedBy} ${formatDateFromUnix(
                extra.approvedDateTime
              )}`
            : extra.approvedBy
          : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: extra.status! ? extra.status! : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: extra.remark ? extra.remark : "",
        style: ["textBody", "textSmall"],
      },
    ]);
  }
  if (dataBomUsageExtras.length > 0) {
    tableBomUsageExtras.push([
      {
        text: "รายการเบิกเพิ่ม",
        style: "textSubTitle",
      },
      {
        style: "tableBodyP4-2",
        table: {
          widths: [250, 110, 110, 50, 205],
          body: [
            [
              {
                text: "รายการเบิก",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ขอเบิกโดย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "อนุมัติเบิกโดย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "สถานะ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "หมายเหตุ",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataBomUsageExtras,
          ],
        },
      },
    ]);
  }

  // BOM Usage packageReasonCodes
  const dataBomUsagePackageReasonCodes = [];
  const tableBomUsagePackageReasonCodes = [];
  for (const packageReasonCode of props.packageReasonCodes) {
    const reasons = packageReasonCode.reasonCodes
      ? JSON.parse(packageReasonCode.reasonCodes)
      : [];
    const totalScrap = ceil2Decimal(
      reasons.reduce((prev: any, cur: any) => prev + Number(cur.quantity), 0)
    );
    const reasonsList = () => {
      return reasons
        .filter((reason: any) => Number(reason.quantity) > 0)
        .map((res: any) => {
          return `• ${res.reasonDescription ? res.reasonDescription : "-"} ${
            res.quantity ? formatNumberV2(res.quantity) : "-"
          } ${packageReasonCode.unit ? packageReasonCode.unit : "-"}\n`;
        });
    };
    if (totalScrap > 0) {
      dataBomUsagePackageReasonCodes.push([
        {
          text: `${
            packageReasonCode.materialDescription
              ? packageReasonCode.materialDescription
              : "-"
          } (${
            packageReasonCode.labOffice ? packageReasonCode.labOffice : "-"
          })`,
          style: ["textBody", "textSmall"],
        },
        {
          text: packageReasonCode.materialCode
            ? packageReasonCode.materialCode
            : "-",
          style: ["textBody", "textSmall"],
        },
        {
          text: reasonsList(),
          style: ["textBody", "textSmall"],
        },
        {
          text: totalScrap,
          style: ["textBody", "textSmall"],
        },
        {
          text: `${
            packageReasonCode.confirmedBy
              ? packageReasonCode.confirmedDate
                ? `${
                    packageReasonCode.confirmedBy.split("@")[0]
                  } (${formatDateISO(packageReasonCode.confirmedDate)}`
                : packageReasonCode.confirmedBy
              : "-"
          }`,
          style: ["textBody", "textSmall"],
        },
      ]);
    }
  }
  if (dataBomUsagePackageReasonCodes.length > 0) {
    tableBomUsagePackageReasonCodes.push([
      {
        text: "เหตุผลของเสีย (ใช้เสีย)",
        style: "textSubTitle",
      },
      {
        style: "tableBodyP4-3",
        table: {
          widths: [270, 65, 270, 50, 70],
          body: [
            [
              {
                text: "Material Description",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Material Code",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Reason Description",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Total Scrap",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Confirmed",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataBomUsagePackageReasonCodes,
          ],
        },
      },
    ]);
  }

  // BOM Usage wasteRejectPkgMvs
  const dataBomUsageWasteRejectPkgMvs = [];
  const tableBomUsageWasteRejectPkgMvs = [];
  for (const wasteRejectPkgMv of props.wasteRejectPkgMvs) {
    const totalReject = ceil2Decimal(
      wasteRejectPkgMv.rejectLots.reduce(
        (prev: any, cur: any) => prev + Number(cur.returnWasteQty),
        0
      )
    );
    const firstLot = wasteRejectPkgMv.rejectLots[0];
    const wasteRejectPkgMvList = () => {
      return wasteRejectPkgMv.rejectLots.map((wrprl: any) => {
        return `• LOT : ${wrprl.batchLotNo ? wrprl.batchLotNo : "-"} ${
          wrprl.returnWasteQty ? formatNumberV2(wrprl.returnWasteQty) : "-"
        } ${wrprl.materialUnit ? wrprl.materialUnit : "-"}\nบันทึกโดย ${
          wrprl.recordedBy ? wrprl.recordedBy : "-"
        } (${
          wrprl.recordedDateTime
            ? formatDateFromUnix(wrprl.recordedDateTime)
            : "-"
        })\nอนุมัติโดย QC ${
          wrprl.approvedWasteReturnBy ? wrprl.approvedWasteReturnBy : "-"
        } (${
          wrprl.approvedWasteReturnDateTime
            ? formatDateFromUnix(wrprl.approvedWasteReturnDateTime)
            : "-"
        })\n`;
      });
    };
    dataBomUsageWasteRejectPkgMvs.push([
      {
        text: `${
          firstLot.materialDescription ? firstLot.materialDescription : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
      {
        text: wasteRejectPkgMv.materialCode
          ? wasteRejectPkgMv.materialCode
          : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: wasteRejectPkgMvList(),
        style: ["textBody", "textSmall"],
      },
      {
        text: `${totalReject ? formatNumberV2(totalReject) : "-"} ${
          firstLot.materialUnit ? firstLot.materialUnit : "-"
        }`,
        style: ["textBody", "textSmall"],
      },
    ]);
  }
  if (dataBomUsageWasteRejectPkgMvs.length > 0) {
    tableBomUsageWasteRejectPkgMvs.push([
      {
        text: "รายการ Reject PKG (คืนเสีย)",
        style: "textSubTitle",
      },
      {
        style: "tableBodyP4-4",
        table: {
          widths: [300, 70, 300, 55],
          body: [
            [
              {
                text: "Material Description",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Material Code",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Reject Details",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Total Reject",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataBomUsageWasteRejectPkgMvs,
          ],
        },
      },
    ]);
  }

  // BOM Usage returns
  const dataBomUsageReturns = [];
  const tableBomUsageReturns = [];
  for (const ret of props.returns) {
    const returnCal = () => {
      return ret
        .extraReturnItems!.filter(
          (retItm: any) =>
            (retItm?.qty || retItm?.withShQty || retItm?.wasteQty!) > 0
        )
        .map((retItm: any) => {
          let returnType = "ปกติ";
          if (retItm?.withShQty! > 0) {
            returnType = "ติด SH/STK";
          }
          if (retItm?.wasteQty! > 0) {
            returnType = "เสีย Reject";
          }
          return `${retItm?.materialCode} - ${retItm?.materialDescription}
				Batch Lot : ${retItm?.batchLotNo}
				Qty : ${formatNumberV2(retItm?.qty || retItm?.withShQty || retItm?.wasteQty)} ${
            retItm?.materialUnit
          } ${returnType}
				${
          retItm?.returnStorageLocation
            ? retItm?.returnStorageLocation
            : retItm?.wasteQty! > 0
            ? "LREJ"
            : "Loc เดิม"
        }`;
        });
    };
    dataBomUsageReturns.push([
      {
        text: ret.requestedBy ? ret.requestedBy : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: ret.requestedDateTime
          ? formatDateFromUnix(ret.requestedDateTime)
          : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: ret.approvedBy ? ret.approvedBy : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: ret.approvedDateTime
          ? formatDateFromUnix(ret.approvedDateTime)
          : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: ret.remark ? ret.remark : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: ret.documentNo ? ret.documentNo : "-",
        style: ["textBody", "textSmall"],
      },
      {
        text: returnCal(),
        style: ["textBody", "textSmall"],
      },
    ]);
  }
  if (dataBomUsageReturns.length > 0) {
    tableBomUsageReturns.push([
      {
        text: "รายการคืนของ",
        style: "textSubTitle",
      },
      {
        style: "tableBodyP4-5",
        table: {
          widths: [80, 70, 80, 70, 100, 50, 255],
          body: [
            [
              {
                text: "คืนโดย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "คืนเมื่อ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "รับโดย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "รับเมื่อ",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Header Text",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Mat. Doc",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "รายการคืน",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataBomUsageReturns,
          ],
        },
      },
    ]);
  }

  // COMMENTS
  const dataComments = [];
  for (const [indexComment, comment] of props.comments.entries()) {
    dataComments.push({
      text: `${indexComment + 1}.${
        comment.commentedBy ? comment.commentedBy : "-"
      } • ${comment.createdAt ? formatDateISOToTh(comment.createdAt) : "-"}\n${
        comment.commentText ? comment.commentText : "-"
      }`,
      style: "textBody",
      marginTop: 10,
    });
  }
  const tableComments = [];
  if (dataComments.length > 0) {
    tableComments.push(
      { text: "", pageBreak: "before" },
      {
        text: "COMMENTS",
        style: "textTitle",
      },
      dataComments
    );
  }

  const dataOrderReceipts = props.orderReceipts.filter((row : OrderReceipt) => !row._deleted)
  const isFilling = props.addOnAndSortedMvs.filter((itm: any) => itm.materialType === 'BULK').length > 0
  
  const pdfDocument = {
    pageMargins: [30, 90, 30, 16],
    header: {
      style: "tableHeader",
      table: {
        widths: [120, 80, 90, 90, 90],
        body: [
          [
            {
              image: "logo_neo_f",
              width: 100,
              style: "tableImgLogo",
              border: [false, false, false, false],
              rowSpan: 3,
            },
            {
              text: "Production Order",
              style: "textHeaderBold",
            },
            {
              text: `${props.order.id ? props.order.id : "-"}`,
              style: "textHeader",
            },
            {
              text: "Quantity",
              style: "textHeaderBold",
            },
            {
              text: `${
                props.order.orderQty
                  ? formatNumberV2(props.order.orderQty || 0)
                  : "-"
              } ${props.order.materialUnit ? props.order.materialUnit : "-"}`,
              style: "textHeader",
            },
          ],
          [
            "",
            {
              text: "Material",
              style: "textHeaderBold",
            },
            {
              text: `${
                props.order.materialDescription
                  ? props.order.materialDescription
                  : "-"
              }`,
              style: "textHeader",
              colSpan: 3,
            },
            "",
            "",
          ],
          [
            "",
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
          ],
        ],
      },
    },
    footer: function (currentPage: any, pageCount: any) {
      return [
        {
          text: `${currentPage.toString()} of ${pageCount}`,
          alignment: "center",
        },
      ];
    },
    content: [
      {
        text: "GERERAL INFORMATION",
        style: "textTitle",
      },
      {
        style: "tableBodyP1",
        marginTop: 10,
        table: {
          widths: [100, 145, 105, 145],
          body: [
            [
              {
                text: "Order No",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${props.order.id ? props.order.id : "-"}`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Material Code",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.materialCode ? props.order.materialCode : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Material Desc",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.materialDescription
                    ? props.order.materialDescription
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Barcode EAN",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.barcodeFgCase! ? props.order.barcodeFgCase! : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Division",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${props.order.division! ? props.order.division! : "-"}`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Qty / Actual",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.orderQty
                    ? formatNumberV2(props.order.orderQty || 0)
                    : "-"
                } / ${
                  props.actualQty ? formatNumberV2(props.actualQty) : "-"
                } ${props.order.materialUnit ? props.order.materialUnit : "-"}`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Released Date",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.createdAt
                    ? formatDateISO(props.order.createdAt)
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Released By",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.releasedBy! ? props.order.releasedBy! : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Actual Due Date",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.actualDueDate!
                    ? formatDate(props.order.actualDueDate!)
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Actual Batch Lots",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.actualBatchNumbers
                    ? (
                        JSON.parse(props.order.actualBatchNumbers) as string[]
                      ).join(", ")
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Trial Batch",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.trialBatch! ? props.order.trialBatch! : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Order Status",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.statusFromApp!
                    ? convertOrderStatus(props.order.statusFromApp!)
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "Product Profle ",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.infoFwt
                    ? props.order.infoFwt
                    : props.order.infoEffBulkDate
                    ? props.order.infoEffBulkDate
                    : props.order.infoRefPkg
                    ? props.order.infoRefPkg
                    : props.order.infoPallet
                    ? props.order.infoPallet
                    : props.order.infoPacking
                    ? props.order.infoPacking
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "Picklist Status",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklistStatus!
                    ? convertPicklistStatus(props.picklistStatus!)
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.infoEffBulkDate
                    ? props.order.infoEffBulkDate
                    : props.order.infoRefPkg
                    ? props.order.infoRefPkg
                    : props.order.infoPallet
                    ? props.order.infoPallet
                    : props.order.infoPacking
                    ? props.order.infoPacking
                    : ""
                }`,
                style: "textBody",
                border: [false, false, false, false],
                colSpan: 3,
              },
              "",
              "",
            ],
            [
              {
                text: "",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.infoRefPkg
                    ? props.order.infoRefPkg
                    : props.order.infoPallet
                    ? props.order.infoPallet
                    : props.order.infoPacking
                    ? props.order.infoPacking
                    : ""
                }`,
                style: "textBody",
                border: [false, false, false, false],
                colSpan: 3,
              },
              "",
              "",
            ],
            [
              {
                text: "",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.infoPallet
                    ? props.order.infoPallet
                    : props.order.infoPacking
                    ? props.order.infoPacking
                    : ""
                }`,
                style: "textBody",
                border: [false, false, false, false],
                colSpan: 3,
              },
              "",
              "",
            ],
            [
              {
                text: "",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.order.infoPacking ? props.order.infoPacking : ""
                }`,
                style: "textBody",
                border: [false, false, false, false],
                colSpan: 3,
              },
              "",
              "",
            ],
          ],
        },
      },
      { text: "", pageOrientation: "landscape", pageBreak: "before" },
      {
        text: "PRODUCTION LOGS",
        style: "textTitle",
      },
      dataOrderConfirmOp,
      tableInspection,
      { text: "", pageOrientation: "landscape", pageBreak: "before" },
      {
        text: "BOM USAGE",
        style: "textTitle",
      },
      {
        style: "tableBodyP4-1",
        marginTop: 10,
        table: {
          widths: [50, 100, 50, 50, 70, 50, 50, 50, 55, 50, 50, 35],
          body: [
            [
              {
                text: "รหัส Mat",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ชื่อ Mat",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "เบิก",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "จ่าย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "เวลาจ่าย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ใช้ดี",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "ใช้เสีย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "คืนดี",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "คืนดีติดSH/STK",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "คืนเสีย",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "Batch No",
                style: ["textBodyBold", "textSmall"],
              },
              {
                text: "หมายเหตุ",
                style: ["textBodyBold", "textSmall"],
              },
            ],
            ...dataAddOnAndSortedMvs,
          ],
        },
      },
      tableBomUsageExtras,
      tableBomUsagePackageReasonCodes,
      tableBomUsageWasteRejectPkgMvs,
      tableBomUsageReturns,
      { text: "", pageOrientation: "landscape", pageBreak: "before" },
      { text: "FG Inspections", style: "textTitle" },
      {
        style: "tableBodyP4-1",
        marginTop: 10,
        table: {
          widths: [100, 50, 20, 100, 50, 80, 100, '*'],
          body: [
            Array.from(OrderInspectionLogColumns).map(col => ({
              text: col.name,
              style: ["textBodyBold", "textSmall"],
            })),
            ...props.fgInspectionLogs.map((log: OrderInspectionLog) => [
              {
                text: `${log.workCenterId} - ${log.workCenterDescription}`,
                style: ["textBody", "textSmall"]
              },
              {
                text: log.inspectionType.split('_')[1],
                style: ["textBody", "textSmall"]
              },
              {
                text: log.inspectionType.split('_')[0],
                style: ["textBody", "textSmall"]
              },
              {
                text: log.avgFwtByLots ? (JSON.parse(log.avgFwtByLots) as AvgFwtByLot[]).map(avg => `${avg.BATCH_LOT}=${formatNumberDigit(avg.AVG_FWT, 2)}`).join('\n') : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: log.inspectionStatus,
                style: ["textBody", "textSmall"]
              },
              {
                text: log.submittedDatetime
                  ? formatDateFromUnix(log.submittedDatetime)
                  : "-",
                style: ["textBody", "textSmall"]
              },
              {
                text: log.submittedBy,
                style: ["textBody", "textSmall"]
              },
              {
                text: 'click',
                link: log.formUrl,
                decoration:"underline",
                style: ["textBody", "textSmall"],
              },
            ]),
          ],
        }
      },
      { text: "", pageOrientation: "landscape", pageBreak: "before" },
      { text: "Order Receipts", style: "textTitle" },
      {
        style: "tableBodyP4-1",
        marginTop: 10,
        table: {
          widths: [50, 30, 40, 50, 50, 40, 40, 40, 40, 40, '*', '*'],
          body: [
            Array.from(OrderReceiptColumns).splice(0, 12).map(col => ({
              text: col.name,
              style: ["textBodyBold", "textSmall"],
            })),
            ...dataOrderReceipts.map((receipt: OrderReceipt) => [
              {
                text: receipt.batchLotNo,
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.avgFillingWeight ? formatNumberDigit(receipt.avgFillingWeight, 2) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.bulkUsage ? formatNumberV2(receipt.bulkUsage) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.totalAmount ? formatNumberV2(receipt.totalAmount) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: formatNumberDigit(isFilling ? calculateYieldFilling(receipt) : calculateYieldPacking(dataOrderReceipts, props.order.orderQty), 2),
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.retainedIssue ? formatNumberV2(receipt.retainedIssue) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.microIssue ? formatNumberV2(receipt.microIssue) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.validateIssue ? formatNumberV2(receipt.validateIssue) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.shelfLifeIssue ? formatNumberV2(receipt.shelfLifeIssue) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.testIssue ? formatNumberV2(receipt.testIssue) : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.reject ? `${formatNumberV2(receipt.reject)} - ${receipt.remark}` : '-',
                style: ["textBody", "textSmall"]
              },
              {
                text: receipt.otherIssue ? `${formatNumberV2(receipt.otherIssue)} - ${receipt.otherIssueNote}` : '-',
                style: ["textBody", "textSmall"]
              }
            ]),
          ],
        }
      },
      { text: "", pageOrientation: "portrait", pageBreak: "before" },
      {
        text: "ACTIVITY LOGS",
        style: "textTitle",
      },
      {
        style: "tableBodyP5",
        marginTop: 10,
        table: {
          widths: [110, 140, 110, 130],
          body: [
            [
              {
                text: "ออกเบิกโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklist.releasedBy!
                    ? props.picklist.releasedDate!
                      ? `${props.picklist.releasedBy!.split("@")[0]}\n${props
                          .picklist.releasedDate!}`
                      : props.picklist.releasedBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "อนุมัติเบิกโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklist.requestedBy!
                    ? props.picklist.requestedDateTime
                      ? `${
                          props.picklist.requestedBy!.split("@")[0]
                        }\n${formatDateFromUnix(
                          props.picklist.requestedDateTime
                        )}`
                      : props.picklist.requestedBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "อนุมัติจ่ายโดย - WH",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklist.approvedBy!
                    ? props.picklist.approvedDateTime
                      ? `${
                          props.picklist.approvedBy!.split("@")[0]
                        }\n${formatDateFromUnix(
                          props.picklist.approvedDateTime
                        )}`
                      : props.picklist.approvedBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "ตรวจรับของโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklist.checkedBy!
                    ? props.picklist.checkedDateTime
                      ? `${
                          props.picklist.checkedBy!.split("@")[0]
                        }\n${formatDateFromUnix(
                          props.picklist.checkedDateTime
                        )}`
                      : props.picklist.checkedBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "ตรวจก่อน Mix โดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklist.qualityCheckedBy
                    ? props.picklist.qualityCheckedDateTime
                      ? `${
                          props.picklist.qualityCheckedBy.split("@")[0]
                        }\n${formatDateFromUnix(
                          props.picklist.qualityCheckedDateTime
                        )}`
                      : props.picklist.qualityCheckedBy.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "เริ่มผลิตโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.firstLog.createdBy!
                    ? props.firstLog.startedTime
                      ? `${
                          props.firstLog.createdBy!.split("@")[0]
                        }\n${formatDateFromUnix(props.firstLog.startedTime)}`
                      : props.firstLog.createdBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "ผลิตจบโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.latestDraftedOp.draftedBy!
                    ? props.latestDraftedOp.draftedDate
                      ? `${
                          props.latestDraftedOp.draftedBy!.split("@")[0]
                        }\n${formatDateISO(
                          props.latestDraftedOp.draftedDate,
                          true
                        )}`
                      : props.latestDraftedOp.draftedBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "ยืนยันการผลิตโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.lastOp.confirmedBy!
                    ? props.lastOp.confirmedDate
                      ? `${
                          props.lastOp.confirmedBy!.split("@")[0]
                        }\n${formatDateISO(props.lastOp.confirmedDate, true)}`
                      : props.lastOp.confirmedBy!.split("@")[0]
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
            [
              {
                text: "อนุมัติของคืน/ปิดเอกสารโดย - PD",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.picklist.completedBy!
                    ? props.picklist.completedDateTime
                      ? `${props.picklist.completedBy!}\n${formatDateFromUnix(
                          props.picklist.completedDateTime
                        )}`
                      : props.picklist.completedBy!
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
              {
                text: "อนุมัติรับคืนโดย - WH",
                style: "textBodyBold",
                border: [false, false, false, false],
              },
              {
                text: `${
                  props.returns.length > 0
                    ? props.returns[props.returns.length - 1].approvedBy!
                      ? props.returns[props.returns.length - 1].approvedDateTime
                        ? `${
                            props.returns[
                              props.returns.length - 1
                            ].approvedBy!.split("@")[0]
                          }\n${formatDateFromUnix(
                            props.returns[props.returns.length - 1]
                              .approvedDateTime
                          )}`
                        : props.returns[
                            props.returns.length - 1
                          ].approvedBy!.split("@")[0]
                      : "-"
                    : "-"
                }`,
                style: "textBody",
                border: [false, false, false, false],
              },
            ],
          ],
        },
      },
      tableComments,
    ],
    styles: {
      tableHeader: {
        marginTop: 30,
        marginLeft: 30,
      },
      textHeaderBold: {
        bold: true,
        fontSize: 14,
      },
      textHeader: {
        fontSize: 14,
      },
      textTitle: {
        alignment: "center",
        bold: true,
        fontSize: 20,
      },
      textBodyBold: {
        bold: true,
        fontSize: 18,
        lineHeight: 0.7,
      },
      textBody: {
        fontSize: 18,
        lineHeight: 0.7,
      },
      textSubTitle: {
        bold: true,
        fontSize: 18,
        marginTop: 20,
        decoration: "underline",
      },
      textSmall: {
        fontSize: 12,
      },
      textGreen: {
        color: "#4CC417",
      },
      textRed: {
        color: "red",
      },
      textYellow: {
        color: "#F6BE00",
      },
    },
    images: {
      logo_neo_f: logoNeoF,
    },
  };

  return pdfDocument;
}

const convertOrderStatus = (status: String) => {
  let label = "";
  if (status === "PD_READY") label = "พร้อมผลิต";
  else if (status === "PD_IN_PROGRESS") label = "กำลังผลิต";
  else if (status === "PD_DRAFTED") label = "ผลิตจบแล้ว";
  else if (status === "PD_CONFIRMED") label = "ยืนยันแล้ว";
  else if (status === "PD_CANCELED") label = "ยกเลิก";
  return label;
};

const convertPicklistStatus = (status: String) => {
  let label = "";
  if (status === "PD_RELEASED") label = "ออกเบิกแล้ว";
  else if (status === "PD_REQUESTED") label = "อนุมัติเบิกแล้ว";
  else if (status === "WH_SHORTAGE") label = "อนุมัติจ่ายแล้ว";
  else if (status === "WH_APPROVED") label = "อนุมัติจ่ายบางส่วน";
  else if (status === "PD_CHECKED") label = "ตรวจสอบแล้ว";
  else if (status === "QA_CHECKED") label = "ตรวจคุณภาพแล้ว";
  else if (status === "PD_RECORDED") label = "บันทึกยอดใช้แล้ว";
  else if (status === "PICKLIST_COMPLETED") label = "ปิดเอกสารแล้ว";
  return label;
};

const getRemainTime = (log: DgtPdOrderOperationLog) => {
  const remainTime =
    getMinuteTimeV2(log.startedTime, log.finishedTime) -
    (log.downtimeBaseTime ? log.downtimeBaseTime : 0);
  return remainTime < 0 ? 0 : remainTime;
};

export default pdfDocumentFormat;
